import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Clock, Lock, ShieldCheck, Sparkles, User, Mail, Phone, Eye, EyeOff, MapPin, AlertCircle, Calendar, Truck, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import Navbar from "../components/Navbar";
import { RentalCreditBox } from "../components/RentalCreditBox";
import Footer from "../components/Footer";
import { useCart } from "../contexts/CartContext";
import { useDeliveryConfig } from "../contexts/SiteTextsContext";
import UpsellBanner from "../components/UpsellBanner";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { trackEvent, gtagBeginCheckout } from "../lib/analytics";
import { toast } from "sonner";
import axios from "axios";
import { getDisplayName, getDisplayImage } from "../utils/variationDisplay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_CODES = [
  { code: "+61", country: "AU", label: "Australia", digits: [9, 10] },
  { code: "+91", country: "IN", label: "India", digits: [10, 10] },
  { code: "+1", country: "US", label: "USA / Canada", digits: [10, 10] },
  { code: "+44", country: "UK", label: "United Kingdom", digits: [10, 11] },
  { code: "+64", country: "NZ", label: "New Zealand", digits: [8, 10] },
  { code: "+65", country: "SG", label: "Singapore", digits: [8, 8] },
  { code: "+60", country: "MY", label: "Malaysia", digits: [9, 11] },
  { code: "+971", country: "AE", label: "UAE", digits: [7, 9] },
  { code: "+94", country: "LK", label: "Sri Lanka", digits: [9, 10] },
  { code: "+86", country: "CN", label: "China", digits: [11, 11] },
];

function validateFullName(name) {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 && parts.every(p => p.length >= 1);
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
}

function validatePhone(phone, codeObj) {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return false;
  const [min, max] = codeObj?.digits || [7, 15];
  return digits.length >= min && digits.length <= max;
}

function getSessionId() {
  return localStorage.getItem("mahra_session_id") || "";
}

function useCountdown(expiresAt) {
  const [timeLeft, setTimeLeft] = useState(null);
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      setTimeLeft(diff > 0 ? diff : 0);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  if (timeLeft === null) return null;
  const mins = Math.floor(timeLeft / 60000);
  const secs = Math.floor((timeLeft % 60000) / 1000);
  return { mins, secs, expired: timeLeft <= 0, raw: timeLeft };
}

const AUTH_MODES = [
  { key: "guest", label: "Continue as Guest" },
  { key: "signin", label: "Sign In" },
  { key: "register", label: "Create Account" },
];

function FieldError({ error }) {
  if (!error) return null;
  return (
    <p className="flex items-center gap-1 mt-1 text-[11px] text-red-500" data-testid="field-error">
      <AlertCircle className="w-3 h-3 flex-shrink-0" />
      {error}
    </p>
  );
}

function formatSlotTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  const ampm = hr >= 12 ? "PM" : "AM";
  const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return `${hr12}:${m} ${ampm}`;
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { fetchCart, cartGroups, cartTotals, cartCount, fetchCartCount, updateCartItem } = useCart();
  const { auspost_content_visible } = useDeliveryConfig();
  const { customer, login } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [creditApplied, setCreditApplied] = useState(null);
  const [checkoutResult, setCheckoutResult] = useState(null);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", phoneCode: "+61",
    address: "", suburb: "", state: "", postcode: "", country: "Australia"
  });
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [authMode, setAuthMode] = useState("guest");
  const [authForm, setAuthForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [createAccountAfter, setCreateAccountAfter] = useState(false);
  const [expired, setExpired] = useState(false);
  const [postcodeStatus, setPostcodeStatus] = useState(null);
  const [switchingToPickup, setSwitchingToPickup] = useState(false);
  const formRef = useRef(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // OTP verification state
  const [emailOtp, setEmailOtp] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [phoneOtpLoading, setPhoneOtpLoading] = useState(false);
  const [emailVerifyLoading, setEmailVerifyLoading] = useState(false);
  const [phoneVerifyLoading, setPhoneVerifyLoading] = useState(false);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);
  const [phoneResendCooldown, setPhoneResendCooldown] = useState(0);

  // Return appointment state
  const [returnSlots, setReturnSlots] = useState([]);
  const [selectedReturn, setSelectedReturn] = useState({ date: "", time: "" });
  const [pickupSlots, setPickupSlots] = useState([]);
  const [selectedPickup, setSelectedPickup] = useState({ date: "", time: "" });
  const [loadingSlots, setLoadingSlots] = useState(false);

  const countdown = useCountdown(checkoutResult?.payment_expires_at);

  const needsDeliveryAddress = cartGroups.some(g =>
    g.main.delivery_method === "delivery" || g.addons.some(a => a.delivery_method === "delivery")
  );

  // Section 5 — flag any cart item whose event_date is today or tomorrow in
  // Australia/Melbourne. Those bookings are pickup-only; the checkout blocks
  // submission if any of them still carries delivery_method === "delivery".
  const isSameOrNextDayMel = (dateStr) => {
    if (!dateStr) return false;
    try {
      const [y, m, d] = String(dateStr).slice(0, 10).split("-").map(Number);
      const nowMel = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
      const todayMel = new Date(nowMel.getFullYear(), nowMel.getMonth(), nowMel.getDate());
      const evMel = new Date(y, m - 1, d);
      const delta = Math.round((evMel - todayMel) / (1000 * 60 * 60 * 24));
      return delta >= 0 && delta <= 1;
    } catch { return false; }
  };
  const pickupOnlyItems = cartGroups.flatMap(g => [g.main, ...g.addons]).filter(i => isSameOrNextDayMel(i.event_date));
  const hasPickupOnlyGroup = pickupOnlyItems.length > 0;
  const pickupOnlyStillMarkedDelivery = pickupOnlyItems.some(i => i.delivery_method === "delivery");

  const allItemsReady = cartGroups.length > 0 && cartGroups.every(g => {
    const all = [g.main, ...g.addons];
    return all.every(item => item.event_date && item.delivery_method);
  });

  useEffect(() => { fetchCart().finally(() => setLoading(false)); trackEvent("checkout_started"); gtagBeginCheckout(cartTotals?.grand_total || 0); }, [fetchCart]);

  // Fetch pickup and return appointment slots once cart is loaded
  useEffect(() => {
    if (cartGroups.length === 0) return;
    const allItems = cartGroups.flatMap(g => [g.main, ...g.addons]);
    const eventDates = allItems.map(i => i.event_date).filter(Boolean).sort();
    const earliestEvent = eventDates[0];
    const latestEvent = eventDates[eventDates.length - 1];
    if (!earliestEvent || !latestEvent) return;

    (async () => {
      setLoadingSlots(true);
      try {
        const [pickupRes, returnRes] = await Promise.all([
          axios.get(`${API}/appointments/available-slots?appointment_type=pickup&event_date=${earliestEvent}`),
          axios.get(`${API}/appointments/available-slots?appointment_type=dropoff&event_date=${latestEvent}`),
        ]);
        setPickupSlots(pickupRes.data.dates || []);
        setReturnSlots(returnRes.data.dates || []);
      } catch { /* silent */ }
      finally { setLoadingSlots(false); }
    })();
  }, [cartGroups]);

  // Restore checkout state from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("mahra_checkout_state");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const expiresAt = new Date(parsed.payment_expires_at).getTime();
        if (expiresAt > Date.now()) {
          setCheckoutResult(parsed);
        } else {
          handleExpiry(parsed.booking_ids);
        }
      } catch { localStorage.removeItem("mahra_checkout_state"); }
    }
  }, []);

  // Save checkout state
  useEffect(() => {
    if (checkoutResult) {
      localStorage.setItem("mahra_checkout_state", JSON.stringify(checkoutResult));
    }
  }, [checkoutResult]);

  // Pre-fill from customer auth
  useEffect(() => {
    if (customer) {
      setFormData(prev => ({
        ...prev,
        name: customer.name || prev.name,
        email: customer.email || prev.email,
        phone: customer.phone || prev.phone,
      }));
      setAuthMode("guest");
    }
  }, [customer]);

  // Autofill from localStorage (returning customers)
  useEffect(() => {
    const saved = localStorage.getItem("mahra_checkout_details");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData(prev => ({
          ...prev,
          name: customer?.name || parsed.name || prev.name,
          email: customer?.email || parsed.email || prev.email,
          phone: customer?.phone || parsed.phone || prev.phone,
          phoneCode: parsed.phoneCode || prev.phoneCode,
          address: parsed.address || prev.address,
          suburb: parsed.suburb || prev.suburb,
          state: parsed.state || prev.state,
          postcode: parsed.postcode || prev.postcode,
          country: parsed.country || prev.country,
        }));
      } catch { /* silent */ }
    }
  }, [customer]);

  // Save form data to localStorage
  useEffect(() => {
    if (formData.name || formData.email || formData.phone) {
      localStorage.setItem("mahra_checkout_details", JSON.stringify(formData));
    }
  }, [formData]);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!needsDeliveryAddress) return;

    const initAutocomplete = () => {
      if (!addressInputRef.current || autocompleteRef.current) return;
      if (!window.google?.maps?.places) return;

      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        { types: ["address"], componentRestrictions: { country: "au" } }
      );
      autocompleteRef.current.setFields(["address_components", "formatted_address"]);
      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (!place.address_components) return;

        let streetNumber = "", route = "", suburb = "", state = "", postcode = "", country = "";
        for (const comp of place.address_components) {
          const types = comp.types;
          if (types.includes("street_number")) streetNumber = comp.long_name;
          if (types.includes("route")) route = comp.long_name;
          if (types.includes("locality") || types.includes("sublocality_level_1")) suburb = comp.long_name;
          if (types.includes("administrative_area_level_1")) state = comp.short_name;
          if (types.includes("postal_code")) postcode = comp.long_name;
          if (types.includes("country")) country = comp.long_name;
        }
        const street = streetNumber ? `${streetNumber} ${route}` : route;
        setFormData(prev => ({
          ...prev,
          address: street.trim(),
          suburb,
          state,
          postcode,
          country: country || "Australia",
        }));
        setErrors(prev => ({ ...prev, address: undefined }));
      });
    };

    if (window.google?.maps?.places) {
      setTimeout(initAutocomplete, 200);
    } else {
      const interval = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(interval);
          initAutocomplete();
        }
      }, 500);
      return () => clearInterval(interval);
    }
  }, [needsDeliveryAddress]);

  // Real-time postcode validation for delivery orders
  useEffect(() => {
    if (!needsDeliveryAddress || !formData.postcode || formData.postcode.length < 4) {
      setPostcodeStatus(null);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/service-area/check?postcode=${formData.postcode}`).then(res => {
      if (!cancelled) {
        setPostcodeStatus({
          valid: res.data.is_serviced,
          message: res.data.is_serviced
            ? "We deliver to your area."
            : (res.data.message || "Outside our delivery zone. Please choose Pick Up instead.")
        });
      }
    }).catch(() => {
      if (!cancelled) setPostcodeStatus(null);
    });
    return () => { cancelled = true; };
  }, [formData.postcode, needsDeliveryAddress]);

  const handleSwitchToPickup = async () => {
    setSwitchingToPickup(true);
    try {
      const deliveryItems = cartGroups.flatMap(g =>
        [g.main, ...g.addons].filter(i => i.delivery_method === "delivery")
      );
      for (const item of deliveryItems) {
        await updateCartItem(item.id, { delivery_method: "pickup" });
      }
      await fetchCart();
      setPostcodeStatus(null);
      toast.success("Switched to Pick Up — delivery fee removed");
    } catch {
      toast.error("Failed to switch. Please try again.");
    } finally {
      setSwitchingToPickup(false);
    }
  };

  // Handle timer expiry
  const handleExpiry = useCallback(async (bookingIds) => {
    try {
      await axios.post(`${API}/cart/expire-reservation`, {
        booking_ids: bookingIds || [],
      }, { headers: { "x-session-id": getSessionId() } });
    } catch { /* silent */ }
    localStorage.removeItem("mahra_checkout_state");
    setCheckoutResult(null);
    setExpired(true);
    fetchCartCount();
  }, [fetchCartCount]);

  useEffect(() => {
    if (countdown?.expired && checkoutResult) {
      handleExpiry(checkoutResult.booking_ids);
    }
  }, [countdown?.expired, checkoutResult, handleExpiry]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    if (authMode === "register" && !authForm.phone?.trim()) {
      toast.error("Phone number is required");
      return;
    }
    setAuthLoading(true);
    try {
      const endpoint = authMode === "register" ? "/customer/auth/register" : "/customer/auth/login";
      const body = authMode === "register"
        ? { name: authForm.name, email: authForm.email, password: authForm.password, phone: authForm.phone }
        : { email: authForm.email, password: authForm.password };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Authentication failed");
      login({ customer_id: data.customer_id, email: data.email, name: data.name, phone: data.phone || "" }, data.session_token);
      setFormData(prev => ({
        ...prev,
        name: data.name || prev.name,
        email: data.email || prev.email,
        phone: data.phone || prev.phone,
      }));
      toast.success(authMode === "register" ? "Account created!" : "Welcome back!");
      setAuthMode("guest");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const selectedCodeObj = COUNTRY_CODES.find(c => c.code === formData.phoneCode) || COUNTRY_CODES[0];

  // --- OTP Functions ---
  // Resend cooldown timers
  useEffect(() => {
    if (emailResendCooldown <= 0) return;
    const t = setTimeout(() => setEmailResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [emailResendCooldown]);

  useEffect(() => {
    if (phoneResendCooldown <= 0) return;
    const t = setTimeout(() => setPhoneResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phoneResendCooldown]);

  // Auto-check if already verified (returning user)
  useEffect(() => {
    if (customer) {
      setEmailVerified(true);
      setPhoneVerified(true);
    }
  }, [customer]);

  // Reset verification when email/phone changes
  useEffect(() => { if (!customer) { setEmailVerified(false); setEmailOtpSent(false); setEmailOtp(""); } }, [formData.email]);
  useEffect(() => { if (!customer) { setPhoneVerified(false); setPhoneOtpSent(false); setPhoneOtp(""); } }, [formData.phone, formData.phoneCode]);

  const sendEmailOtp = async () => {
    if (!validateEmail(formData.email)) { toast.error("Enter a valid email first"); return; }
    setEmailOtpLoading(true);
    try {
      const res = await axios.post(`${API}/otp/send-email-otp`, { email: formData.email.trim() });
      if (res.data.success) {
        setEmailOtpSent(true);
        setEmailResendCooldown(30);
        toast.success("Verification code sent to your email");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send code");
    }
    setEmailOtpLoading(false);
  };

  const verifyEmailOtp = async () => {
    if (!emailOtp.trim() || emailOtp.trim().length < 6) { toast.error("Enter the 6-digit code"); return; }
    setEmailVerifyLoading(true);
    try {
      const res = await axios.post(`${API}/otp/verify-email-otp`, { email: formData.email.trim(), otp: emailOtp.trim() });
      if (res.data.verified) {
        setEmailVerified(true);
        toast.success("Email verified!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    }
    setEmailVerifyLoading(false);
  };

  const sendPhoneOtp = async () => {
    if (!validatePhone(formData.phone, selectedCodeObj)) { toast.error("Enter a valid phone number first"); return; }
    setPhoneOtpLoading(true);
    try {
      const fullPhone = `${formData.phoneCode}${formData.phone.replace(/\D/g, "")}`;
      const res = await axios.post(`${API}/otp/send-sms-otp`, { phone: fullPhone });
      if (res.data.success) {
        setPhoneOtpSent(true);
        setPhoneResendCooldown(30);
        toast.success("Verification code sent via SMS");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send SMS");
    }
    setPhoneOtpLoading(false);
  };

  const verifyPhoneOtp = async () => {
    if (!phoneOtp.trim() || phoneOtp.trim().length < 6) { toast.error("Enter the 6-digit code"); return; }
    setPhoneVerifyLoading(true);
    try {
      const fullPhone = `${formData.phoneCode}${formData.phone.replace(/\D/g, "")}`;
      const res = await axios.post(`${API}/otp/verify-sms-otp`, { phone: fullPhone, otp: phoneOtp.trim() });
      if (res.data.verified) {
        setPhoneVerified(true);
        toast.success("Phone number verified!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    }
    setPhoneVerifyLoading(false);
  };

  const needsVerification = !customer; // Only guests need verification

  const validate = () => {
    const errs = {};
    if (!validateFullName(formData.name)) {
      errs.name = "Please enter your full name (first and last name)";
    }
    if (!validateEmail(formData.email)) {
      errs.email = "Please enter a valid email (e.g. name@email.com)";
    }
    if (!validatePhone(formData.phone, selectedCodeObj)) {
      const [min, max] = selectedCodeObj.digits;
      errs.phone = min === max
        ? `Please enter a ${min}-digit phone number`
        : `Please enter a ${min}-${max} digit phone number`;
    }
    if (needsDeliveryAddress && !formData.address.trim()) {
      errs.address = "Please enter a delivery address";
    }
    if (needsDeliveryAddress && postcodeStatus && !postcodeStatus.valid) {
      errs.address = "Outside our delivery zone. Please choose Pick Up in the cart instead.";
    }
    if (!selectedPickup.date || !selectedPickup.time) {
      errs.pickupSlot = "Please select a collection appointment time";
    }
    if (!selectedReturn.date || !selectedReturn.time) {
      errs.returnSlot = "Please select a return appointment time";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCheckout = async () => {
    if (!validate()) {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    // Block checkout if guest and not verified
    if (needsVerification && !emailVerified) {
      toast.error("Please verify your email address before placing your order");
      return;
    }
    if (needsVerification && !phoneVerified) {
      toast.error("Please verify your phone number before placing your order");
      return;
    }
    if (!termsAccepted) { toast.error("Please accept the rental terms"); return; }

    // Section 5 — client-side guard. Backend enforces this too.
    if (pickupOnlyStillMarkedDelivery) {
      toast.error("Same-day and next-day bookings are pickup-only. Please change delivery method to pickup.");
      return;
    }

    setSubmitting(true);
    try {
      const fullPhone = `${formData.phoneCode} ${formData.phone}`;
      const res = await axios.post(`${API}/cart/checkout`, {
        customer_name: formData.name.trim(),
        customer_email: formData.email.trim(),
        customer_phone: fullPhone,
        delivery_address: formData.address,
        postcode: formData.postcode,
        suburb: formData.suburb,
        state: formData.state,
        country: formData.country,
        terms_accepted_at: new Date().toISOString(),
        terms_accepted_ip: "client",
        return_appointment_date: selectedReturn.date,
        return_appointment_time: selectedReturn.time,
        pickup_appointment_date: selectedPickup.date,
        pickup_appointment_time: selectedPickup.time,
      }, { headers: { "x-session-id": getSessionId() } });

      setCheckoutResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayNow = useCallback(async () => {
    if (!checkoutResult?.booking_ids?.length) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/payments/create-cart-session`, {
        booking_ids: checkoutResult.booking_ids,
        origin_url: window.location.origin,
        rental_credit: creditApplied?.credit_number || "",
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment setup failed");
      setSubmitting(false);
    }
  }, [checkoutResult, creditApplied]);

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-AU", { weekday: "short", month: "short", day: "numeric" });
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <Navbar />
      <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#9A7B4F]" /></div>
    </div>
  );

  // Reservation expired view
  if (expired) {
    return (
      <div className="min-h-screen bg-[#FAF8F0]">
        <Navbar />
        <div className="max-w-[480px] mx-auto px-4 py-16 text-center" data-testid="checkout-expired">
          <div className="w-16 h-16 rounded-full bg-[#282828]/5 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-7 h-7 text-[#676664]/40" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl text-[#1A1A1A] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Your reservation time has expired.
          </h2>
          <p className="text-[14px] text-[#676664]/60 leading-relaxed mb-8">
            The selected items have been released and returned to availability.
            <br />Please re-add them to your cart to continue booking.
          </p>
          <Button onClick={() => navigate("/")} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-8 py-4 text-[11px] tracking-[0.15em] uppercase" data-testid="browse-after-expiry-btn">
            Browse Collection
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  // Phase 2: After booking created - timer + pay
  if (checkoutResult) {
    return (
      <div className="min-h-screen bg-[#FAF8F0]">
        <Navbar />
        <div className="max-w-[560px] mx-auto px-4 py-12">
          <div className={`text-center p-5 mb-6 border ${countdown?.raw < 120000 ? 'border-red-300 bg-red-50' : 'border-[#9A7B4F]/20 bg-[#9A7B4F]/[0.04]'}`} data-testid="checkout-timer">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className={`w-4 h-4 ${countdown?.raw < 120000 ? 'text-red-500' : 'text-[#9A7B4F]'}`} strokeWidth={1.5} />
              <span className="text-[10px] tracking-[0.12em] uppercase font-medium text-[#676664]">Reservation Expires In</span>
            </div>
            <p className={`text-3xl font-semibold tracking-wider ${countdown?.raw < 120000 ? 'text-red-600' : 'text-[#282828]'}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {String(countdown?.mins || 0).padStart(2, "0")}:{String(countdown?.secs || 0).padStart(2, "0")}
            </p>
            <p className="text-[11px] text-[#676664]/50 mt-1">Items are held for 15 minutes</p>
          </div>
          <div className="bg-white border border-[#282828]/8 p-5 mb-4" data-testid="checkout-summary">
            <h3 className="text-[16px] text-[#282828] font-medium tracking-[0.05em] uppercase mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
              Order Confirmed - {checkoutResult.item_count} {checkoutResult.item_count === 1 ? "item" : "items"}
            </h3>
            {creditApplied && (
              <>
                <div className="flex justify-between pt-3 text-[13px]" data-testid="checkout-rental-line">
                  <span className="text-[#676664]">Rental fees</span>
                  <span className="text-[#282828]">${creditApplied.rental_total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-1 text-[13px]" data-testid="checkout-credit-line">
                  <span className="text-[#676664]">Mahra Rental Credit (rental only)</span>
                  <span className="text-[#0E7C5A] font-medium">−${creditApplied.applied.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-1 pb-2 text-[12px] text-[#676664]/70" data-testid="checkout-other-line">
                  <span>Bond & delivery (unchanged)</span>
                  <span>${(creditApplied.total_before - creditApplied.rental_total).toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between pt-3 border-t border-[#282828]/10 text-[15px]">
              <span className="text-[#282828] font-medium">Total Due</span>
              <span className="text-[#9A7B4F] font-semibold text-[20px]" data-testid="checkout-total-due">${(creditApplied ? creditApplied.total_after : checkoutResult.total_amount).toFixed(2)}</span>
            </div>
          </div>
          <RentalCreditBox bookingIds={checkoutResult.booking_ids} applied={creditApplied} onApplied={setCreditApplied} />
          <Button onClick={handlePayNow} disabled={submitting || countdown?.expired} className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-5 text-[12px] tracking-[0.15em] uppercase" data-testid="pay-now-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : `Pay Now - $${(creditApplied ? creditApplied.total_after : checkoutResult.total_amount).toFixed(2)}`}
          </Button>
          <div className="flex items-center justify-center gap-1.5 mt-4 text-[10px] text-[#676664]/40">
            <Lock className="w-3 h-3" /> Secure payment via Stripe
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Phase 1: Checkout form
  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <Navbar />
      <div className="max-w-[900px] mx-auto px-4 lg:px-6 py-8 lg:py-12">
        <h1 className="text-2xl lg:text-3xl text-[#1A1A1A] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Checkout
        </h1>

        {cartCount === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#676664]/50">Your cart is empty</p>
            <Button onClick={() => navigate("/")} variant="outline" className="mt-4 text-[11px] tracking-[0.1em] uppercase">Browse Collection</Button>
          </div>
        ) : !allItemsReady ? (
          <div className="text-center py-16" data-testid="checkout-missing-dates">
            <AlertCircle className="w-12 h-12 text-[#9A7B4F]/40 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[16px] text-[#282828] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Missing Booking Details
            </p>
            <p className="text-[13px] text-[#676664]/60 mb-6 max-w-md mx-auto">
              Please ensure all items have rental dates and delivery/pickup options selected before proceeding.
            </p>
            <Button onClick={() => navigate("/cart")} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-8 py-3 text-[11px] tracking-[0.12em] uppercase" data-testid="back-to-cart-btn">
              Return to Cart
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
            <div ref={formRef}>
              {/* Auth Mode Selector */}
              {!customer && (
                <div className="bg-white border border-[#282828]/8 p-5 mb-4" data-testid="checkout-auth-selector">
                  <div className="flex gap-0 border border-[#282828]/10 overflow-hidden">
                    {AUTH_MODES.map(m => (
                      <button
                        key={m.key}
                        onClick={() => setAuthMode(m.key)}
                        className={`flex-1 py-2.5 text-[10px] tracking-[0.08em] uppercase transition-colors ${authMode === m.key ? 'bg-[#282828] text-white' : 'bg-white text-[#676664] hover:bg-[#FAF8F0]'}`}
                        data-testid={`auth-mode-${m.key}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>

                  {/* Sign In Form */}
                  {authMode === "signin" && (
                    <form onSubmit={handleAuthSubmit} className="mt-5 space-y-3" data-testid="checkout-signin-form">
                      <button type="button" onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 py-2.5 border border-[#282828]/10 text-[12px] text-[#282828] hover:bg-[#FAF8F0] transition-colors" data-testid="checkout-google-btn">
                        <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                        Continue with Google
                      </button>
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-px bg-[#282828]/10" /><span className="text-[9px] text-[#676664]/40 uppercase">or</span><div className="flex-1 h-px bg-[#282828]/10" />
                      </div>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
                        <Input type="email" placeholder="Email" value={authForm.email} onChange={e => setAuthForm(f => ({ ...f, email: e.target.value }))} className="pl-10 py-4 border-[#282828]/10 text-[13px]" required data-testid="checkout-auth-email" />
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
                        <Input type={showPassword ? "text" : "password"} placeholder="Password" value={authForm.password} onChange={e => setAuthForm(f => ({ ...f, password: e.target.value }))} className="pl-10 pr-10 py-4 border-[#282828]/10 text-[13px]" required data-testid="checkout-auth-password" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#676664]/30">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <Button type="submit" disabled={authLoading} className="w-full bg-[#282828] hover:bg-[#1a1a1a] text-white py-4 text-[11px] tracking-[0.12em] uppercase" data-testid="checkout-auth-submit">
                        {authLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Sign In"}
                      </Button>
                    </form>
                  )}

                  {/* Create Account Form */}
                  {authMode === "register" && (
                    <form onSubmit={handleAuthSubmit} className="mt-5 space-y-3" data-testid="checkout-register-form">
                      <button type="button" onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 py-2.5 border border-[#282828]/10 text-[12px] text-[#282828] hover:bg-[#FAF8F0] transition-colors" data-testid="checkout-google-register-btn">
                        <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                        Sign up with Google
                      </button>
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-px bg-[#282828]/10" /><span className="text-[9px] text-[#676664]/40 uppercase">or</span><div className="flex-1 h-px bg-[#282828]/10" />
                      </div>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
                        <Input placeholder="Full name" value={authForm.name} onChange={e => setAuthForm(f => ({ ...f, name: e.target.value }))} className="pl-10 py-4 border-[#282828]/10 text-[13px]" required data-testid="checkout-reg-name" />
                      </div>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
                        <Input type="email" placeholder="Email" value={authForm.email} onChange={e => setAuthForm(f => ({ ...f, email: e.target.value }))} className="pl-10 py-4 border-[#282828]/10 text-[13px]" required data-testid="checkout-reg-email" />
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
                        <Input type={showPassword ? "text" : "password"} placeholder="Password" value={authForm.password} onChange={e => setAuthForm(f => ({ ...f, password: e.target.value }))} className="pl-10 pr-10 py-4 border-[#282828]/10 text-[13px]" required minLength={6} data-testid="checkout-reg-password" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#676664]/30">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
                        <Input placeholder="Phone (optional)" value={authForm.phone} onChange={e => setAuthForm(f => ({ ...f, phone: e.target.value }))} className="pl-10 py-4 border-[#282828]/10 text-[13px]" data-testid="checkout-reg-phone" />
                      </div>
                      <Button type="submit" disabled={authLoading} className="w-full bg-[#282828] hover:bg-[#1a1a1a] text-white py-4 text-[11px] tracking-[0.12em] uppercase" data-testid="checkout-reg-submit">
                        {authLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Create Account & Continue"}
                      </Button>
                    </form>
                  )}
                </div>
              )}

              {/* Logged-in indicator */}
              {customer && (
                <div className="bg-[#9A7B4F]/[0.04] border border-[#9A7B4F]/15 p-3 mb-4 flex items-center gap-2" data-testid="checkout-logged-in">
                  <ShieldCheck className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                  <span className="text-[12px] text-[#676664]">Signed in as <strong className="text-[#282828]">{customer.name || customer.email}</strong></span>
                </div>
              )}

              {/* Contact Details Form */}
              {(authMode === "guest" || customer) && (
                <div className="bg-white border border-[#282828]/8 p-6" data-testid="checkout-form">
                  <h3 className="text-[16px] text-[#282828] font-medium tracking-[0.05em] uppercase mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                    Contact Details
                  </h3>
                  <div className="space-y-4">
                    {/* Full Name */}
                    <div>
                      <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">Full Name *</label>
                      <Input
                        value={formData.name}
                        onChange={e => { setFormData(p => ({ ...p, name: e.target.value })); setErrors(p => ({ ...p, name: undefined })); }}
                        placeholder="First Last"
                        className={`border-[#282828]/15 ${errors.name ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                        data-testid="checkout-name"
                      />
                      <FieldError error={errors.name} />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">Email *</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={e => { setFormData(p => ({ ...p, email: e.target.value })); setErrors(p => ({ ...p, email: undefined })); }}
                        placeholder="name@email.com"
                        className={`border-[#282828]/15 ${errors.email ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                        data-testid="checkout-email"
                      />
                      <FieldError error={errors.email} />

                      {/* Email OTP verification (guest only) */}
                      {needsVerification && validateEmail(formData.email) && (
                        <div className="mt-2" data-testid="email-otp-section">
                          {emailVerified ? (
                            <div className="flex items-center gap-1.5 text-green-600">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              <span className="text-[11px] font-medium">Email verified</span>
                            </div>
                          ) : !emailOtpSent ? (
                            <button
                              type="button"
                              onClick={sendEmailOtp}
                              disabled={emailOtpLoading}
                              className="text-[11px] text-[#9A7B4F] hover:text-[#8a6b3f] font-medium flex items-center gap-1 transition-colors disabled:opacity-50"
                              data-testid="send-email-otp-btn"
                            >
                              {emailOtpLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                              Send verification code
                            </button>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <Input
                                  value={emailOtp}
                                  onChange={e => setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                  placeholder="Enter 6-digit code"
                                  maxLength={6}
                                  className="flex-1 border-[#282828]/15 text-center tracking-[4px] text-sm font-medium"
                                  autoFocus
                                  data-testid="email-otp-input"
                                />
                                <Button
                                  type="button"
                                  onClick={verifyEmailOtp}
                                  disabled={emailVerifyLoading || emailOtp.length < 6}
                                  className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-[10px] px-4"
                                  data-testid="verify-email-otp-btn"
                                >
                                  {emailVerifyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Verify"}
                                </Button>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] text-[#676664]/40">Check your inbox for the code</span>
                                <button
                                  type="button"
                                  onClick={sendEmailOtp}
                                  disabled={emailResendCooldown > 0 || emailOtpLoading}
                                  className="text-[10px] text-[#9A7B4F] hover:underline disabled:text-[#676664]/30 disabled:no-underline"
                                  data-testid="resend-email-otp-btn"
                                >
                                  {emailResendCooldown > 0 ? `Resend in ${emailResendCooldown}s` : "Resend code"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Phone with Country Code */}
                    <div>
                      <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">Phone *</label>
                      <div className="flex gap-2">
                        <select
                          value={formData.phoneCode}
                          onChange={e => setFormData(p => ({ ...p, phoneCode: e.target.value }))}
                          className="flex-shrink-0 w-[115px] h-9 px-2 text-[13px] border border-[#282828]/15 bg-white rounded-md focus:border-[#9A7B4F] focus:outline-none focus:ring-1 focus:ring-[#9A7B4F]/20 cursor-pointer"
                          data-testid="checkout-phone-code"
                        >
                          {COUNTRY_CODES.map(cc => (
                            <option key={cc.code} value={cc.code}>
                              {cc.code} {cc.country}
                            </option>
                          ))}
                        </select>
                        <Input
                          value={formData.phone}
                          onChange={e => {
                            const val = e.target.value.replace(/[^\d\s\-]/g, '');
                            setFormData(p => ({ ...p, phone: val }));
                            setErrors(p => ({ ...p, phone: undefined }));
                          }}
                          placeholder={selectedCodeObj.country === "AU" ? "0412 345 678" : "Phone number"}
                          className={`flex-1 border-[#282828]/15 ${errors.phone ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                          data-testid="checkout-phone"
                        />
                      </div>
                      <FieldError error={errors.phone} />

                      {/* Phone OTP verification (guest only) */}
                      {needsVerification && validatePhone(formData.phone, selectedCodeObj) && (
                        <div className="mt-2" data-testid="phone-otp-section">
                          {phoneVerified ? (
                            <div className="flex items-center gap-1.5 text-green-600">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              <span className="text-[11px] font-medium">Phone verified</span>
                            </div>
                          ) : !phoneOtpSent ? (
                            <button
                              type="button"
                              onClick={sendPhoneOtp}
                              disabled={phoneOtpLoading}
                              className="text-[11px] text-[#9A7B4F] hover:text-[#8a6b3f] font-medium flex items-center gap-1 transition-colors disabled:opacity-50"
                              data-testid="send-phone-otp-btn"
                            >
                              {phoneOtpLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Phone className="w-3 h-3" />}
                              Send verification code
                            </button>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <Input
                                  value={phoneOtp}
                                  onChange={e => setPhoneOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                  placeholder="Enter 6-digit code"
                                  maxLength={6}
                                  className="flex-1 border-[#282828]/15 text-center tracking-[4px] text-sm font-medium"
                                  autoFocus
                                  data-testid="phone-otp-input"
                                />
                                <Button
                                  type="button"
                                  onClick={verifyPhoneOtp}
                                  disabled={phoneVerifyLoading || phoneOtp.length < 6}
                                  className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-[10px] px-4"
                                  data-testid="verify-phone-otp-btn"
                                >
                                  {phoneVerifyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Verify"}
                                </Button>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] text-[#676664]/40">Check your phone for the SMS code</span>
                                <button
                                  type="button"
                                  onClick={sendPhoneOtp}
                                  disabled={phoneResendCooldown > 0 || phoneOtpLoading}
                                  className="text-[10px] text-[#9A7B4F] hover:underline disabled:text-[#676664]/30 disabled:no-underline"
                                  data-testid="resend-phone-otp-btn"
                                >
                                  {phoneResendCooldown > 0 ? `Resend in ${phoneResendCooldown}s` : "Resend code"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Create account option */}
                  {!customer && authMode === "guest" && (
                    <label className="flex items-center gap-2.5 mt-5 pt-4 border-t border-[#282828]/8 cursor-pointer" data-testid="create-account-after">
                      <input type="checkbox" checked={createAccountAfter} onChange={e => setCreateAccountAfter(e.target.checked)} className="rounded border-[#282828]/20 w-4 h-4 text-[#9A7B4F] focus:ring-[#9A7B4F]" />
                      <span className="text-[12px] text-[#676664]">Create an account for faster checkout next time</span>
                    </label>
                  )}

                  {/* Delivery Address with Google Places Autocomplete */}
                  {needsDeliveryAddress && (
                    <div className="mt-6 pt-5 border-t border-[#282828]/8">
                      <h3 className="text-[16px] text-[#282828] font-medium tracking-[0.05em] uppercase mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                        Delivery Address
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">Street Address *</label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30 z-10" />
                            <input
                              ref={addressInputRef}
                              type="text"
                              value={formData.address}
                              onChange={e => { setFormData(p => ({ ...p, address: e.target.value })); setErrors(p => ({ ...p, address: undefined })); }}
                              placeholder="Start typing your address..."
                              className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-[13px] shadow-sm transition-colors pl-10 focus:outline-none focus:ring-1 focus:ring-[#9A7B4F]/20 focus:border-[#9A7B4F] ${errors.address ? 'border-red-400' : 'border-[#282828]/15'}`}
                              data-testid="checkout-address"
                            />
                          </div>
                          <FieldError error={errors.address} />
                          <p className="text-[10px] text-[#676664]/40 mt-1">Select from suggestions to autofill address fields</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">Suburb</label>
                            <Input value={formData.suburb} onChange={e => setFormData(p => ({ ...p, suburb: e.target.value }))} className="border-[#282828]/15" data-testid="checkout-suburb" />
                          </div>
                          <div>
                            <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">State</label>
                            <Input value={formData.state} onChange={e => setFormData(p => ({ ...p, state: e.target.value }))} className="border-[#282828]/15" data-testid="checkout-state" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">Postcode</label>
                            <Input value={formData.postcode} onChange={e => setFormData(p => ({ ...p, postcode: e.target.value }))} className={`border-[#282828]/15 ${postcodeStatus && !postcodeStatus.valid ? 'border-red-400' : postcodeStatus?.valid ? 'border-green-500' : ''}`} data-testid="checkout-postcode" />
                            {postcodeStatus && !postcodeStatus.valid && (
                              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded" data-testid="postcode-outside-zone">
                                <p className="text-[11px] text-red-600 mb-2">{postcodeStatus.message}</p>
                                <button
                                  onClick={handleSwitchToPickup}
                                  disabled={switchingToPickup}
                                  className="w-full py-2 px-3 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] tracking-[0.1em] uppercase rounded transition-colors disabled:opacity-50"
                                  data-testid="switch-to-pickup-btn"
                                >
                                  {switchingToPickup ? "Switching..." : "Switch to Pick Up & Save $" + (cartTotals?.delivery || 15).toFixed(0)}
                                </button>
                              </div>
                            )}
                            {postcodeStatus && postcodeStatus.valid && (
                              <p className="text-[10px] mt-1 text-green-600" data-testid="postcode-status">{postcodeStatus.message}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-[11px] text-[#676664]/50 uppercase tracking-wider block mb-1.5">Country</label>
                            <Input value={formData.country} onChange={e => setFormData(p => ({ ...p, country: e.target.value }))} className="border-[#282828]/15" data-testid="checkout-country" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Appointment Pickers */}
                  <div className="mt-6 pt-5 border-t border-[#282828]/8">
                    {/* Collection/Pickup Appointment Picker */}
                    <div className="mb-6 pb-5 border-b border-[#282828]/8">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                        <h3 className="text-[16px] text-[#282828] font-medium tracking-[0.05em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                          {needsDeliveryAddress ? "Delivery" : "Collection"} Appointment *
                        </h3>
                      </div>
                      <p className="text-[11px] text-[#676664]/50 mb-4">
                        {needsDeliveryAddress ? "Select a time for your outfit to be delivered before your event." : "Select a time to collect your outfit before your event."}
                      </p>

                      {loadingSlots ? (
                        <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>
                      ) : pickupSlots.length === 0 ? (
                        <div className="text-center py-4 bg-[#FAF8F0] border border-[#282828]/8">
                          <Calendar className="w-6 h-6 text-[#676664]/20 mx-auto mb-2" strokeWidth={1} />
                          <p className="text-[12px] text-[#676664]/50">No slots available. Please contact us.</p>
                        </div>
                      ) : (
                        <div className="space-y-3" data-testid="pickup-slots-container">
                          {pickupSlots.map((dateGroup) => (
                            <div key={dateGroup.date} className="border border-[#282828]/8 bg-white" data-testid={`pickup-date-${dateGroup.date}`}>
                              <div className="px-4 py-2.5 border-b border-[#282828]/6 bg-[#FAF8F0]">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={1.5} />
                                  <span className="text-[12px] text-[#282828] font-medium">{dateGroup.day_label}</span>
                                </div>
                              </div>
                              <div className="p-3">
                                {dateGroup.times.length === 0 ? (
                                  <p className="text-[11px] text-[#676664]/50 text-center py-2">All slots booked</p>
                                ) : (
                                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`pickup-times-${dateGroup.date}`}>
                                    {dateGroup.times.map((time) => {
                                      const isSelected = selectedPickup.date === dateGroup.date && selectedPickup.time === time;
                                      return (
                                        <button
                                          type="button"
                                          key={time}
                                          onClick={() => setSelectedPickup({ date: dateGroup.date, time })}
                                          className={`py-2 px-1.5 text-[11px] border transition-all ${isSelected
                                            ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]'
                                            : 'bg-white text-[#5A5A5A] border-[#282828]/12 hover:border-[#9A7B4F]/40'}`}
                                          data-testid={`pickup-slot-${dateGroup.date}-${time}`}
                                        >
                                          {formatSlotTime(time)}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          {selectedPickup.date && selectedPickup.time && (
                            <div className="bg-[#9A7B4F]/[0.06] border border-[#9A7B4F]/15 p-3 flex items-center gap-2" data-testid="selected-pickup-info">
                              <Clock className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={1.5} />
                              <span className="text-[12px] text-[#282828]">
                                {needsDeliveryAddress ? "Delivery" : "Collection"}: {pickupSlots.find(s => s.date === selectedPickup.date)?.day_label || selectedPickup.date} at {formatSlotTime(selectedPickup.time)}
                              </span>
                            </div>
                          )}
                          <FieldError error={errors.pickupSlot} />
                        </div>
                      )}
                    </div>

                    {/* Return Appointment Picker */}
                    <div className="mb-6 pb-5 border-b border-[#282828]/8">
                      <div className="flex items-center gap-2 mb-1">
                        <Truck className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                        <h3 className="text-[16px] text-[#282828] font-medium tracking-[0.05em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                          Return Appointment *
                        </h3>
                      </div>
                      <p className="text-[11px] text-[#676664]/50 mb-4">
                        {needsDeliveryAddress ? "Select a time for your outfit to be collected after your event." : "Select a time to drop off your outfit after your event."}
                      </p>

                      {loadingSlots ? (
                        <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" /></div>
                      ) : returnSlots.length === 0 ? (
                        <div className="text-center py-4 bg-[#FAF8F0] border border-[#282828]/8">
                          <Calendar className="w-6 h-6 text-[#676664]/20 mx-auto mb-2" strokeWidth={1} />
                          <p className="text-[12px] text-[#676664]/50">No return slots available. Please contact us.</p>
                        </div>
                      ) : (
                        <div className="space-y-3" data-testid="return-slots-container">
                          {returnSlots.map((dateGroup) => (
                            <div key={dateGroup.date} className="border border-[#282828]/8 bg-white" data-testid={`return-date-${dateGroup.date}`}>
                              <div className="px-4 py-2.5 border-b border-[#282828]/6 bg-[#FAF8F0]">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={1.5} />
                                  <span className="text-[12px] text-[#282828] font-medium">{dateGroup.day_label}</span>
                                </div>
                              </div>
                              <div className="p-3">
                                {dateGroup.times.length === 0 ? (
                                  <p className="text-[11px] text-[#676664]/50 text-center py-2">All slots booked</p>
                                ) : (
                                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`return-times-${dateGroup.date}`}>
                                    {dateGroup.times.map((time) => {
                                      const isSelected = selectedReturn.date === dateGroup.date && selectedReturn.time === time;
                                      return (
                                        <button
                                          type="button"
                                          key={time}
                                          onClick={() => setSelectedReturn({ date: dateGroup.date, time })}
                                          className={`py-2 px-1.5 text-[11px] border transition-all ${isSelected
                                            ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]'
                                            : 'bg-white text-[#5A5A5A] border-[#282828]/12 hover:border-[#9A7B4F]/40'}`}
                                          data-testid={`return-slot-${dateGroup.date}-${time}`}
                                        >
                                          {formatSlotTime(time)}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          {selectedReturn.date && selectedReturn.time && (
                            <div className="bg-[#9A7B4F]/[0.06] border border-[#9A7B4F]/15 p-3 flex items-center gap-2" data-testid="selected-return-info">
                              <Clock className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={1.5} />
                              <span className="text-[12px] text-[#282828]">
                                Return: {returnSlots.find(s => s.date === selectedReturn.date)?.day_label || selectedReturn.date} at {formatSlotTime(selectedReturn.time)}
                              </span>
                            </div>
                          )}
                          <FieldError error={errors.returnSlot} />
                        </div>
                      )}
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer" data-testid="checkout-terms">
                      <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="mt-0.5 rounded border-[#282828]/20 w-4 h-4 text-[#9A7B4F] focus:ring-[#9A7B4F]" />
                      <span className="text-[12px] text-[#5A5A5A] leading-relaxed">
                        I agree to the rental terms and conditions. I understand the security bond* is fully refundable upon return of items in original condition.
                      </span>
                    </label>
                    <p className="text-[10px] text-[#676664]/50 mt-3 ml-7 leading-relaxed" data-testid="card-auth-disclosure">
                      By completing payment you authorise Mahra to charge your card for late returns, damages, or replacement if required.
                    </p>
                  </div>
                </div>
              )}

              {/* Items preview */}
              <div className="bg-white border border-[#282828]/8 p-5 mt-4" data-testid="checkout-items">
                <h3 className="text-[12px] text-[#676664]/50 tracking-[0.1em] uppercase mb-3">Your Items</h3>
                {cartGroups.map(g => (
                  <div key={g.main.id} className="py-2 border-b border-[#282828]/5 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-12 flex-shrink-0 bg-[#f5f3f0] overflow-hidden">
                        {(() => { const _i = getDisplayImage(g.main, g.main); return _i ? <img src={_i} alt="" className="w-full h-full object-cover" /> : null; })()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-[#282828] truncate">{getDisplayName(g.main, g.main)}</p>
                        <p className="text-[10px] text-[#676664]/40">{formatDate(g.main.event_date)} · {g.main.duration} days</p>
                      </div>
                      <span className="text-[13px] text-[#9A7B4F] font-medium">${g.main.rental_price.toFixed(2)}</span>
                    </div>
                    {g.addons.map(a => (
                      <div key={a.id} className="flex items-center gap-3 pl-6 mt-1.5">
                        <div className="w-7 h-8 flex-shrink-0 bg-[#f5f3f0] overflow-hidden">
                          {(() => { const _i = getDisplayImage(a, a); return _i ? <img src={_i} alt="" className="w-full h-full object-cover" /> : null; })()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Sparkles className="w-2.5 h-2.5 text-[#9A7B4F]" />
                            <p className="text-[11px] text-[#5A5A5A] truncate">{getDisplayName(a, a)}</p>
                          </div>
                        </div>
                        <span className="text-[11px] text-[#9A7B4F] font-medium">${(a.rental_price - (a.discount_amount || 0)).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar Summary */}
            <div className="lg:sticky lg:top-24 self-start">
              <div className="bg-white border border-[#282828]/8 p-5" data-testid="checkout-sidebar">
                <h3 className="text-[16px] text-[#282828] font-medium tracking-[0.05em] uppercase mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                  Order Summary
                </h3>
                <div className="space-y-2.5 text-[13px]">
                  <div className="flex justify-between"><span className="text-[#5A5A5A]">Rental Total</span><span className="text-[#282828] font-medium">${(cartTotals.rental || 0).toFixed(2)}</span></div>
                  {(cartTotals.discount || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-[#9A7B4F]">Discount</span><span className="text-[#9A7B4F] font-medium">-${(cartTotals.discount || 0).toFixed(2)}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-[#5A5A5A]">Security Bonds*</span><span className="text-[#282828] font-medium">${(cartTotals.bond || 0).toFixed(2)}</span></div>
                  <p className="text-[10px] text-[#676664]/45 italic leading-snug">Your bond is 100% refunded within 48 hours of your outfit's safe return.</p>
                  {/* Delivery fee display — per-window breakdown */}
                  {(cartTotals.delivery_windows || []).length > 0 && (
                    <div className="space-y-1">
                      {cartTotals.delivery_windows.map((w, idx) => (
                        <div key={`${w.event_date}-${w.duration}-${idx}`} className="flex justify-between">
                          <span className={w.free_delivery ? "text-[#5A7B5F]" : "text-[#5A5A5A]"}>
                            Delivery ({new Date(w.event_date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" })}, {w.duration}-day)
                          </span>
                          {w.free_delivery ? (
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] text-[#676664]/40 line-through">${(cartTotals.delivery_fee_per_window || 15).toFixed(2)}</span>
                              <span className="text-[#5A7B5F] font-medium">FREE</span>
                            </div>
                          ) : (
                            <span className="text-[#282828] font-medium">${w.fee.toFixed(2)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[9px] text-[#676664]/35 italic">{auspost_content_visible ? "Prepaid Australia Post return label included" : ""}</p>
                  <div className="flex justify-between pt-3 border-t border-[#282828]/10">
                    <span className="text-[#282828] font-medium text-[14px]">Total</span>
                    <span className="text-[#9A7B4F] font-semibold text-[18px]">${(cartTotals.grand_total || 0).toFixed(2)}</span>
                  </div>
                  <p className="text-[10px] text-[#676664]/40">Refundable: ${(cartTotals.refundable || 0).toFixed(2)} (Security Bonds*)</p>
                  <p className="text-[9px] text-[#676664]/30 mt-1">*Fully refundable upon return in original condition</p>
                </div>

                {/* Verification warning for guests */}
                {needsVerification && (!emailVerified || !phoneVerified) && (
                  <div className="bg-amber-50 border border-amber-200 p-3 mb-3 mt-3" data-testid="verification-warning">
                    <p className="text-[11px] text-amber-700 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      Please verify your {!emailVerified && !phoneVerified ? "email and phone number" : !emailVerified ? "email" : "phone number"} to continue
                    </p>
                  </div>
                )}

                {/* Section 5 — pickup-only banner for same/next-day bookings */}
                {hasPickupOnlyGroup && (
                  <div
                    className="bg-[#FAF3E0] border border-[#E7CE9A] p-3 mb-3 mt-3"
                    data-testid="pickup-only-banner"
                  >
                    <p className="text-[11px] text-[#3D2B1F] flex items-start gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Pickup only:</strong> same-day and next-day bookings are collected in-store.
                        Delivery isn&apos;t available for {pickupOnlyItems.length === 1 ? "this event date" : "these event dates"}.
                      </span>
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleCheckout}
                  disabled={submitting || (authMode !== "guest" && !customer) || (needsVerification && (!emailVerified || !phoneVerified)) || pickupOnlyStillMarkedDelivery}
                  className="w-full mt-5 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-4 text-[10px] tracking-[0.15em] uppercase"
                  data-testid="place-order-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Place Order & Reserve"}
                </Button>

                {authMode !== "guest" && !customer && (
                  <p className="text-[10px] text-[#676664]/50 text-center mt-2">Please sign in or continue as guest to place your order</p>
                )}

                <div className="flex items-center gap-2 mt-3">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#9A7B4F]/50" strokeWidth={1.5} />
                  <span className="text-[10px] text-[#676664]/40">15-minute reservation on all items</span>
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <Lock className="w-3.5 h-3.5 text-[#9A7B4F]/50" strokeWidth={1.5} />
                  <span className="text-[10px] text-[#676664]/40">Secure checkout via Stripe</span>
                </div>
              </div>
              <UpsellBanner variant="compact" />
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
